import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useLanguage } from '../../hooks'
import Link from '../common/util/input/Link'
import get from '../helpers/Api'
import {
    formatDiff,
    formatDiffFloor,
    formatNumber,
    formatUSD,
} from '../helpers/FormatLinks'
import PopupWrapperSearch from '../popups/PopupWrapperSearch'

export const AssetDetailsPopupContent = ({ asset, listing, userName }) => {
    const [numAndLowestMint, setNumAndLowestMint] = useState(null)

    const { assetId, template, collection } = asset

    const [searchType, setSearchType] = useState(null)
    const [searchAsset, setSearchAsset] = useState(null)

    const setPopupSearch = (searchType, asset) => {
        setSearchType(searchType)
        setSearchAsset(asset)
    }

    const { price, currency, usdWax, market } = listing
        ? listing
        : {
              price: null,
              currency: null,
              usdWax: null,
              market: null,
          }

    useEffect(() => {
        fetchOwnerShipStats()
    }, [assetId, template?.templateId])

    const fetchOwnerShipStats = async () => {
        if (!numAndLowestMint) {
            if (template?.templateId) {
                get(
                    `ownership-info-template?template_id=${template?.templateId}&owner=${userName}`,
                    'legacy',
                ).then(setNumAndLowestMint)
            } else if (assetId) {
                get(
                    `ownership-info?asset_id=${asset.assetId}&owner=${userName}`,
                    'legacy',
                ).then(setNumAndLowestMint)
            }
        }
    }

    const avgWAX = template?.stats.averageWaxPrice
    const avgUSD = template?.stats.averageUsd
    const lastSold = template?.stats.lastSold?.priceWax
    const lastSoldUSD = template?.stats.lastSold?.priceUsd
    const lowestWAX = template?.stats.floorPrice
    const numSales = template?.stats.numSales

    let diffMedian = 0
    if (price && currency === 'WAX' && avgWAX) diffMedian = price / avgWAX - 1
    if (price && currency === 'USD' && avgUSD) diffMedian = price / avgUSD - 1

    let diffLastSold = 0
    if (price && currency === 'WAX' && lastSold)
        diffLastSold = price / lastSold - 1
    if (price && currency === 'USD' && lastSoldUSD)
        diffLastSold = price / lastSoldUSD - 1

    let diffFloor = 0
    if (price && currency === 'WAX' && lowestWAX) diffFloor = price - lowestWAX
    if (price && currency === 'USD' && lowestWAX)
        diffFloor = price / usdWax - lowestWAX

    const { t } = useLanguage('common')

    return (
        <div
            id={
                'AssetDetailsPopupContent' +
                (assetId ? assetId : template?.templateId)
            }
            onClick={(e) => e.preventDefault()}
            className={'w-100 h-100 '}
        >
            <PopupWrapperSearch
                asset={searchAsset}
                searchType={searchType}
                setSearchType={setSearchType}
            />
            <Table size="small">
                <TableBody>
                    {asset.seller ? (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold">Current Owner</div>
                            </TableCell>
                            <TableCell>
                                <Link href={`/user/${asset.seller}`}>
                                    <div className="text-primary hover:underline">
                                        {asset.seller}
                                    </div>
                                </Link>
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {price && currency === 'WAX' ? (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold">
                                    {t('asset.price')}
                                </div>
                            </TableCell>
                            <TableCell>
                                {formatNumber(price)} WAX /{' '}
                                {formatUSD(price * usdWax)}
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {price && currency === 'USD' ? (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold">
                                    {t('asset.price')}
                                </div>
                            </TableCell>
                            <TableCell>
                                {formatNumber(price / usdWax)} WAX /{' '}
                                {formatUSD(price)}
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {avgWAX && (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold dark:!text-white text-black">
                                    {t('search.median')}
                                </div>
                            </TableCell>
                            <TableCell>
                                {
                                    <div
                                        className={
                                            'flex dark:!text-white text-black'
                                        }
                                    >
                                        {`${formatNumber(
                                            avgWAX,
                                        )} WAX / $${formatNumber(avgUSD)}`}
                                        {formatDiff(
                                            diffMedian,
                                            market,
                                            t('search.median'),
                                        )}
                                    </div>
                                }
                            </TableCell>
                        </TableRow>
                    )}
                    {lastSold && (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold dark:!text-white text-black">
                                    {t('asset.last_sold')}
                                </div>
                            </TableCell>
                            <TableCell>
                                <div
                                    className="cursor-pointer flex text-primary hover:underline"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        setPopupSearch('sales', {
                                            collection:
                                                asset.collection.collectionName,
                                            schema: asset.schema,
                                            templateId:
                                                asset.template?.templateId,
                                        })
                                    }}
                                >
                                    {`${formatNumber(
                                        lastSold,
                                    )} WAX / $${formatNumber(lastSoldUSD)}`}
                                    {formatDiff(
                                        diffLastSold,
                                        market,
                                        t('asset.last_sold'),
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    )}

                    {numSales && (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold dark:!text-white text-black">
                                    {t('search.trades')}
                                </div>
                            </TableCell>
                            <TableCell className="dark:!text-white text-black">
                                {numSales}
                            </TableCell>
                        </TableRow>
                    )}
                    {lowestWAX && (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold dark:!text-white text-black">
                                    {t('search.cheapest')}
                                </div>
                            </TableCell>
                            <TableCell>
                                <div
                                    className="cursor-pointer flex text-primary hover:underline"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        setPopupSearch('listings', {
                                            collection:
                                                asset.collection.collectionName,
                                            schema: asset.schema,
                                            templateId:
                                                asset.template?.templateId,
                                        })
                                    }}
                                >
                                    {`${formatNumber(
                                        lowestWAX,
                                    )} WAX / $${formatNumber(
                                        lowestWAX * usdWax,
                                    )}`}
                                    {formatDiffFloor(
                                        diffFloor,
                                        market,
                                        'Floor',
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    )}
                    {asset.mint ? (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold dark:!text-white text-black">
                                    Mint
                                </div>
                            </TableCell>
                            <TableCell className="dark:!text-white text-black">
                                {asset.mint}
                                {asset.total ? ` / ${asset.total}` : ''}
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {asset.schema ? (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold dark:!text-white text-black">
                                    Schema
                                </div>
                            </TableCell>

                            <TableCell>
                                <Link
                                    href={`/collection/${collection.collectionName}/${asset.schema}`}
                                >
                                    <div className="text-primary hover:underline">
                                        {asset.schema}
                                    </div>
                                </Link>
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {asset.template?.templateId ? (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold dark:!text-white text-black">
                                    Template
                                </div>
                            </TableCell>

                            <TableCell>
                                <Link
                                    href={`/template/${asset.template?.templateId}`}
                                >
                                    <div className="text-primary hover:underline">
                                        {asset.template?.templateId}
                                    </div>
                                </Link>
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {numAndLowestMint && numAndLowestMint.numAssets !== null ? (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold dark:!text-white text-black">
                                    Owned
                                </div>
                            </TableCell>

                            <TableCell>
                                {numAndLowestMint.numAssets > 0 ? (
                                    <Link
                                        href={`/user/${userName}?tab=inventory&owner=${userName}&collection=${asset.collection.collectionName}&term=${asset.template?.templateId}&order_by=mint_asc&search_type=assets#Tabs`}
                                    >
                                        <div className="text-primary hover:underline">
                                            {numAndLowestMint.numAssets}
                                        </div>
                                    </Link>
                                ) : (
                                    <div className="dark:!text-white text-black">
                                        None
                                    </div>
                                )}
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {numAndLowestMint && numAndLowestMint.minMint ? (
                        <TableRow>
                            <TableCell>
                                <div className="font-bold text-white">
                                    Own Lowest Mint
                                </div>
                            </TableCell>
                            <TableCell>
                                <Link
                                    href={`/user/${userName}?tab=inventory&owner=${userName}&collection=${asset.collection.collectionName}&term=${asset.template?.templateId}&order_by=mint_asc&search_type=assets#Tabs`}
                                >
                                    <div className="text-primary hover:underline">
                                        {numAndLowestMint.minMint}
                                    </div>
                                </Link>
                            </TableCell>
                        </TableRow>
                    ) : null}
                </TableBody>
            </Table>
        </div>
    )
}
